// Fast path quality (admin) — three stored sources, three tabs.
//
// "Truth-set runs": the answer truth-set eval ledger
// (fastpath_eval_runs via /api/admin/fastpath-quality/eval-runs) and, for the
// picked run, every case with its verdict, expected facts vs answer, the
// grounding verifier's issues and cost. "Run now" queues the Celery eval task.
// "Watchdog checks": the daily watchdog's judgments of REAL fast-path turns
// (fastpath_quality_checks), one summary row per sweep and the per-turn
// checks of the picked sweep, filterable by verdict.
// "Recipes": the executable fast recipes (fastpath_recipes), their lifecycle
// state, code, verification and newest runs; Disable / Enable / Activate
// move the FSM with a reason, "Verify now" queues the verifier task.
// Nothing is recomputed here — the harness, the watchdog and the recipe
// store own every number.

import { useCallback, useEffect, useState } from 'react';

import {
  fastpathQualityApi,
  type EvalRunDetail,
  type EvalRunRead,
  type RecipeDetail as RecipeDetailRead,
  type RecipeRead,
  type RecipeState,
  type WatchdogCheckRead,
  type WatchdogRunSummary,
} from '../services/fastpathQuality';
import { recordAction } from '../services/guruRecorderBridge';
import { useAuth } from '../context/AuthContext';
import { EvalResultsTable } from './fastpathQuality/EvalResultsTable';
import { EvalRunsTable } from './fastpathQuality/EvalRunsTable';
import { RecipeDetail } from './fastpathQuality/RecipeDetail';
import { RecipesTable } from './fastpathQuality/RecipesTable';
import { WatchdogChecksTable } from './fastpathQuality/WatchdogChecksTable';
import { WatchdogRunsTable } from './fastpathQuality/WatchdogRunsTable';
import { triggerLabel, usd, whenUtc } from './fastpathQuality/format';
import styles from './FastpathQualityPage.module.css';

type Tab = 'eval' | 'watchdog' | 'recipes';
const ACTIVE_POLL_MS = 15_000;
const CHECKS_PAGE = 100;
const RECIPES_PAGE = 200;

export function FastpathQualityPage() {
  const { user } = useAuth();
  const canRun = user?.role === 'ADMIN';
  const [tab, setTab] = useState<Tab>('eval');

  // --- truth-set eval ---
  const [runs, setRuns] = useState<EvalRunRead[]>([]);
  const [detail, setDetail] = useState<EvalRunDetail | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [evalLoading, setEvalLoading] = useState(true);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [runNote, setRunNote] = useState<string | null>(null);
  const [queuing, setQueuing] = useState(false);

  const loadEval = useCallback(async (runId: string | null) => {
    setEvalError(null);
    try {
      const [ledger, picked] = await Promise.all([
        fastpathQualityApi.listEvalRuns(30),
        runId ? fastpathQualityApi.getEvalRun(runId) : fastpathQualityApi.latestEvalRun(),
      ]);
      setRuns(ledger.items);
      setDetail(picked);
      setSelectedId(picked?.id ?? null);
    } catch {
      setEvalError('Could not load the truth-set eval runs — is the API up and are you an admin?');
    } finally {
      setEvalLoading(false);
    }
  }, []);

  useEffect(() => { void loadEval(null); }, [loadEval]);

  const active = runs.some((r) => r.status === 'running');
  useEffect(() => {
    if (!active) return undefined;
    const t = window.setInterval(() => { void loadEval(selectedId); }, ACTIVE_POLL_MS);
    return () => window.clearInterval(t);
  }, [active, loadEval, selectedId]);

  const openRun = useCallback((id: string) => {
    recordAction('fastpath_eval_run_open', { targets: { run_id: id }, outcome: 'request' });
    void loadEval(id);
  }, [loadEval]);

  const runNow = useCallback(async () => {
    recordAction('fastpath_eval_run_now', { outcome: 'request' });
    setQueuing(true);
    setRunNote(null);
    try {
      const res = await fastpathQualityApi.runEval();
      setRunNote(res.detail);
      await loadEval(selectedId);
    } catch {
      setRunNote('Could not queue the eval — are you an admin, and is the worker up?');
    } finally {
      setQueuing(false);
    }
  }, [loadEval, selectedId]);

  // --- watchdog ---
  const [sweeps, setSweeps] = useState<WatchdogRunSummary[]>([]);
  const [sweepId, setSweepId] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<string>('');
  const [checks, setChecks] = useState<WatchdogCheckRead[]>([]);
  const [checksTotal, setChecksTotal] = useState(0);
  const [wdLoading, setWdLoading] = useState(false);
  const [wdLoaded, setWdLoaded] = useState(false);
  const [wdError, setWdError] = useState<string | null>(null);

  const loadWatchdog = useCallback(async (runId: string | null, v: string) => {
    setWdError(null);
    setWdLoading(true);
    try {
      const [list, page] = await Promise.all([
        fastpathQualityApi.listWatchdogRuns(30),
        fastpathQualityApi.listWatchdogChecks({ runId: runId ?? undefined, verdict: v || undefined, limit: CHECKS_PAGE }),
      ]);
      setSweeps(list);
      setChecks(page.items);
      setChecksTotal(page.total);
      setWdLoaded(true);
    } catch {
      setWdError('Could not load the watchdog checks — is the API up and are you an admin?');
    } finally {
      setWdLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'watchdog' && !wdLoaded && !wdLoading) void loadWatchdog(sweepId, verdict);
  }, [tab, wdLoaded, wdLoading, loadWatchdog, sweepId, verdict]);

  // --- recipes ---
  const [recipeRows, setRecipeRows] = useState<RecipeRead[]>([]);
  const [recipeTotal, setRecipeTotal] = useState(0);
  const [recipeFilter, setRecipeFilter] = useState<string>('');
  const [recipe, setRecipe] = useState<RecipeDetailRead | null>(null);
  const [rcLoading, setRcLoading] = useState(false);
  const [rcLoaded, setRcLoaded] = useState(false);
  const [rcError, setRcError] = useState<string | null>(null);
  const [rcBusy, setRcBusy] = useState(false);
  const [rcNote, setRcNote] = useState<string | null>(null);

  const loadRecipes = useCallback(async (state: string) => {
    setRcError(null);
    setRcLoading(true);
    try {
      const page = await fastpathQualityApi.listRecipes({ state: state || undefined, limit: RECIPES_PAGE });
      setRecipeRows(page.items);
      setRecipeTotal(page.total);
      setRcLoaded(true);
    } catch {
      setRcError('Could not load the recipes — is the API up and are you an admin?');
    } finally {
      setRcLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'recipes' && !rcLoaded && !rcLoading) void loadRecipes(recipeFilter);
  }, [tab, rcLoaded, rcLoading, loadRecipes, recipeFilter]);

  const changeRecipeFilter = (state: string) => {
    recordAction('fastpath_recipe_filter', { values: { state: state || 'all' }, outcome: 'request' });
    setRecipeFilter(state);
    void loadRecipes(state);
  };

  const openRecipe = useCallback(async (id: string) => {
    recordAction('fastpath_recipe_open', { targets: { recipe_id: id }, outcome: 'open' });
    setRcNote(null);
    setRcError(null);
    try {
      setRecipe(await fastpathQualityApi.getRecipe(id));
    } catch {
      setRcError('Could not load that recipe.');
    }
  }, []);

  const refreshRecipe = useCallback(async (id: string, state: string) => {
    const [detail] = await Promise.all([fastpathQualityApi.getRecipe(id), loadRecipes(state)]);
    setRecipe(detail);
  }, [loadRecipes]);

  const setRecipeState = useCallback(async (to: RecipeState) => {
    if (!recipe) return;
    const reason = window.prompt(`Reason for moving ${recipe.slug} v${recipe.version} to ${to}:`);
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setRcNote('A reason is required.');
      return;
    }
    recordAction('fastpath_recipe_state', { targets: { recipe_id: recipe.id }, values: { state: to }, outcome: 'request' });
    setRcBusy(true);
    setRcNote(null);
    try {
      await fastpathQualityApi.setRecipeState(recipe.id, to, trimmed);
      await refreshRecipe(recipe.id, recipeFilter);
      setRcNote(`Recipe moved to ${to}.`);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } } | null)?.response?.data?.detail;
      setRcNote(detail ? `Refused: ${detail}` : 'Could not change the recipe state — are you an admin?');
    } finally {
      setRcBusy(false);
    }
  }, [recipe, recipeFilter, refreshRecipe]);

  const verifyRecipe = useCallback(async () => {
    if (!recipe) return;
    recordAction('fastpath_recipe_verify', { targets: { recipe_id: recipe.id }, outcome: 'request' });
    setRcBusy(true);
    setRcNote(null);
    try {
      const res = await fastpathQualityApi.verifyRecipe(recipe.id);
      setRcNote(res.queued ? `Verification queued (task ${res.task_id ?? '?'}); the verdict lands on the recipe when the worker finishes.` : 'Verification was not queued.');
    } catch {
      setRcNote('Could not queue the verification — are you an admin, and is the worker up?');
    } finally {
      setRcBusy(false);
    }
  }, [recipe]);

  const switchTab = (next: Tab) => {
    if (next === tab) return;
    recordAction('fastpath_quality_tab_switch', { values: { tab: next }, outcome: 'open' });
    setTab(next);
  };

  const openSweep = (runId: string) => {
    const next = runId === sweepId ? null : runId;
    recordAction('fastpath_watchdog_run_open', { targets: { run_id: runId }, values: { mode: next ? 'select' : 'clear' }, outcome: 'request' });
    setSweepId(next);
    void loadWatchdog(next, verdict);
  };

  const changeVerdict = (v: string) => {
    recordAction('fastpath_watchdog_verdict_filter', { targets: { run_id: sweepId ?? undefined }, values: { filter: v || 'all' }, outcome: 'request' });
    setVerdict(v);
    void loadWatchdog(sweepId, v);
  };

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1>Fast path quality</h1>
          <p>
            Is Merlin's fast tier answering correctly? Truth-set runs replay fixed questions through the fast
            tier and judge every answer against known facts; watchdog checks judge a sample of real turns
            against the tool results captured in that same turn (a breach raises a HIGH RedFlag).
          </p>
        </div>
        {tab === 'eval' && canRun ? (
          <button type="button" className={styles.runBtn} onClick={runNow} disabled={queuing || active}>
            {active ? 'Eval running…' : queuing ? 'Queuing…' : 'Run now'}
          </button>
        ) : null}
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Fast path quality sections">
        <button type="button" role="tab" aria-selected={tab === 'eval'} className={tab === 'eval' ? styles.tabActive : styles.tab} onClick={() => switchTab('eval')}>
          Truth-set runs
        </button>
        <button type="button" role="tab" aria-selected={tab === 'watchdog'} className={tab === 'watchdog' ? styles.tabActive : styles.tab} onClick={() => switchTab('watchdog')}>
          Watchdog checks
        </button>
        <button type="button" role="tab" aria-selected={tab === 'recipes'} className={tab === 'recipes' ? styles.tabActive : styles.tab} onClick={() => switchTab('recipes')}>
          Recipes
        </button>
      </div>

      {tab === 'eval' ? (
        <div role="tabpanel" aria-label="Truth-set runs panel">
          {runNote ? <div className={styles.runNote}>{runNote}</div> : null}
          {evalError ? <div className={styles.error}>{evalError}</div> : null}
          {evalLoading ? <div className={styles.empty}>Loading…</div> : null}
          {!evalLoading && detail ? (
            <div className={styles.summary} data-testid="eval-summary">
              <span>
                Showing run <code className={styles.tag}>{detail.id.slice(0, 8)}</code> · {triggerLabel(detail.trigger)} ·{' '}
                {detail.status} · started {whenUtc(detail.started_at)} · finished {whenUtc(detail.finished_at)}
              </span>
              <span className={styles.meta}>
                truth set {detail.truth_set_version}{detail.git_sha ? ` @ ${detail.git_sha.slice(0, 7)}` : ''} · judge{' '}
                {detail.judge_model} · {detail.cases_passed}/{detail.cases_total} correct · {detail.cases_partial} partial ·{' '}
                {detail.cases_failed} failed · {detail.cases_handoff} handed off · {detail.cases_error} errored ·{' '}
                {usd(detail.cost_usd)}
              </span>
              {detail.error ? <span className={styles.warn}>{detail.error}</span> : null}
            </div>
          ) : null}
          {!evalLoading && detail ? (
            <EvalResultsTable
              results={detail.results}
              onToggle={(caseId, open) =>
                recordAction('fastpath_eval_case_toggle', { targets: { run_id: detail.id, case_id: caseId }, outcome: open ? 'open' : 'close' })
              }
            />
          ) : null}
          {!evalLoading && !detail && !evalError ? (
            <div className={styles.empty}>No completed eval yet{canRun ? ' — run it now, or wait for the weekly beat.' : '.'}</div>
          ) : null}
          {!evalLoading ? <EvalRunsTable runs={runs} selectedId={selectedId} onSelect={openRun} /> : null}
        </div>
      ) : tab === 'recipes' ? (
        <div role="tabpanel" aria-label="Recipes panel">
          {rcError ? <div className={styles.error}>{rcError}</div> : null}
          {rcLoading && !rcLoaded ? <div className={styles.empty}>Loading…</div> : null}
          {rcLoaded && recipe ? (
            <RecipeDetail
              recipe={recipe}
              canAct={canRun}
              busy={rcBusy}
              note={rcNote}
              onSetState={(to) => { void setRecipeState(to); }}
              onVerify={() => { void verifyRecipe(); }}
            />
          ) : null}
          {rcLoaded ? (
            <RecipesTable
              recipes={recipeRows}
              total={recipeTotal}
              state={recipeFilter}
              selectedId={recipe?.id ?? null}
              onStateChange={changeRecipeFilter}
              onSelect={(id) => { void openRecipe(id); }}
            />
          ) : null}
        </div>
      ) : (
        <div role="tabpanel" aria-label="Watchdog checks panel">
          {wdError ? <div className={styles.error}>{wdError}</div> : null}
          {wdLoading && !wdLoaded ? <div className={styles.empty}>Loading…</div> : null}
          {wdLoaded ? (
            <>
              <WatchdogRunsTable runs={sweeps} selectedId={sweepId} onSelect={openSweep} />
              <WatchdogChecksTable
                checks={checks}
                total={checksTotal}
                verdict={verdict}
                onVerdictChange={changeVerdict}
                runLabel={sweepId ? `sweep ${sweepId.slice(0, 8)}` : 'all sweeps'}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default FastpathQualityPage;
